/** @typedef {{ startTime: number, endTime: number }} StableFragment */
/** @typedef {{ time: number, fragIndex: number }} FragmentSampleSlot */

export const DEFAULT_FRAGMENT_SAMPLE_OPTS = {
    minSamplesPerFragment: 5,
    maxSamplesPerFragment: 11,
    /** 每秒抽检帧数，在 min~max 之间缩放 */
    samplesPerSec: 0.4,
    /** 避开镜头首尾（转场/淡入淡出）的比例 */
    edgeMarginRatio: 0.05,
};

function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
}

/**
 * 为单个镜头生成等间隔抽检时间点（略缩首尾）。
 * 短镜：计划数不超过 `floor(时长 × fps)`，有几帧抽几帧。
 *
 * @param {StableFragment} frag
 * @param {typeof DEFAULT_FRAGMENT_SAMPLE_OPTS} opts
 * @param {number} [fps=30]
 * @returns {number[]}
 */
export function planSampleTimesForFragment(frag, opts = DEFAULT_FRAGMENT_SAMPLE_OPTS, fps = 30) {
    const dur = frag.endTime - frag.startTime;
    if (dur <= 0) {
        return [];
    }

    const safeFps = Number.isFinite(fps) && fps > 0 ? fps : 30;
    const maxFramesInDur = Math.max(1, Math.floor(dur * safeFps));

    let n = clamp(
        Math.round(dur * opts.samplesPerSec),
        opts.minSamplesPerFragment,
        opts.maxSamplesPerFragment,
    );
    n = Math.min(n, maxFramesInDur);

    const margin = dur * opts.edgeMarginRatio;
    const lo = frag.startTime + margin;
    const hi = frag.endTime - margin;
    if (hi <= lo + 1e-9) {
        return Array.from({ length: n }, () => frag.startTime + dur * 0.5);
    }

    return Array.from({ length: n }, (_, i) => lo + ((i + 0.5) / n) * (hi - lo));
}

/**
 * 为全部镜头生成按时间排序的抽检计划。
 * @param {StableFragment[]} fragments
 * @param {Partial<typeof DEFAULT_FRAGMENT_SAMPLE_OPTS>} [opts]
 * @param {number} [fps=30]
 * @returns {FragmentSampleSlot[]}
 */
export function buildFragmentSampleSchedule(fragments, opts = {}, fps = 30) {
    const merged = { ...DEFAULT_FRAGMENT_SAMPLE_OPTS, ...opts };
    /** @type {FragmentSampleSlot[]} */
    const schedule = [];

    fragments.forEach((frag, fragIndex) => {
        for (const time of planSampleTimesForFragment(frag, merged, fps)) {
            schedule.push({ time, fragIndex });
        }
    });

    schedule.sort((a, b) => a.time - b.time);
    return schedule;
}
